/**
 * @file useDealCard.ts
 * @description Данные карточки сделки (phase09; phase19.31): сама сделка,
 * связанный заказ и счётчики вкладок «События» и «Документы».
 *
 * Счётчик «События» — по событиям этой сделки (`dealId`), тем же запросом,
 * что список во вкладке; раньше он считал ленту всего клиента и расходился
 * со списком. Заказ и счётчики — по возможности: их сбой карточку не ломает.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError } from "../../../lib/api-client";
import { getDeal, listActivities, listDocuments, type DealDto } from "../../../lib/api-crm";
import { getOrder, type OrderDto } from "../../../lib/api-orders";

export interface DealCardState {
  deal: DealDto | null;
  order: OrderDto | null;
  eventsCount: number;
  documentsCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const EMPTY_PAGE = { total: 0, items: [] as never[] };

export function useDealCard(id: string | undefined): DealCardState {
  const [deal, setDeal] = useState<DealDto | null>(null);
  const [order, setOrder] = useState<OrderDto | null>(null);
  const [eventsCount, setEventsCount] = useState(0);
  const [documentsCount, setDocumentsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const d = await getDeal(id);
        if (cancelled) return;
        setDeal(d);
        const [events, docs, linked] = await Promise.all([
          listActivities({ dealId: id, limit: 1 }).catch(() => EMPTY_PAGE),
          listDocuments({ dealId: id, limit: 1 }).catch(() => EMPTY_PAGE),
          d.resultingOrderId ? getOrder(d.resultingOrderId).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setEventsCount(events.total);
        setDocumentsCount(docs.total);
        setOrder(linked);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Не удалось загрузить сделку. Попробуйте ещё раз");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, attempt]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);

  return { deal, order, eventsCount, documentsCount, loading, error, refresh };
}
